import { useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import "./scrollReveal.css";

/*
  사이트 공통 등장 애니메이션
  - 화면에 들어올 때 살짝 올라오며 나타납니다.
  - 페이지마다 코드를 고치지 않도록, 공통으로 쓰는 카드/제목/이미지 선택자를 한곳에서 관리합니다.
  - "동작 줄이기"를 켠 사용자나 IntersectionObserver를 지원하지 않는 브라우저에서는 적용하지 않습니다.
*/

const SELECTORS = [
  // 메인 (v3)
  // 첫 화면 문구는 진입 애니메이션(CSS)이 따로 있어 제외합니다.
  ".v3-head", ".v3-biz", ".v3-number", ".v3-sol-card", ".v3-news-card",
  ".contact-banner-left", ".contact-banner-card",
  // 서비스 (RPM · 헬스케어)
  ".rpm-section2-maintext", ".rpm-section2-subtext", ".rpmImage",
  ".rpm-section3-text-layout", ".rpm-section3-group-box",
  ".rpm-section4-maintext", ".svc-card", ".ps-product", ".ps-detail-col", ".ps-evidence",
  ".pd-section", ".pd-col", ".pd-feature", ".pd-validation",
  // 임상 연구
  ".cr-section-header", ".cr-feature-card", ".cr-flow-step",
  ".cr-stat-card", ".cr-compare-col", ".cr-cta-title", ".cr-cta-subtitle", ".cr-cta-buttons",
  // 회사 소개 (신규 구성)
  ".abh-intro-highlight", ".abh-intro-body", ".abh-head",
  ".abh-stat-card", ".abh-consequence", ".abh-solution-card",
  ".abh-benefit-card", ".abh-evidence-item", ".abh-cert-item", ".abh-europe",
  ".abh-research-text", ".abh-research-points", ".abh-cta-title", ".abh-cta-buttons",
  // 회사 소개 (기존)
  ".wwa-section-title", ".wwa-intro-highlight", ".wwa-intro-text",
  ".wwa-mission-text", ".wwa-step", ".wwa-ceo-content",
  ".wwa-business-card", ".wwa-service-card", ".wwa-why-item", ".wwa-case-card",
  ".wwa-cta-title", ".wwa-cta-subtitle", ".wwa-cta-buttons",
  // 팀 · 뉴스 · 문의
  ".heringsTeamWrap", ".deptStructureImg", ".website-card",
  ".contactusInputDiv", ".contractusMapWrap",
].join(",");

const REVEAL_CLASS = "reveal-item";
const SHOWN_CLASS = "is-revealed";

export default function ScrollReveal() {
  const location = useLocation();

  // 화면이 그려지기 전에 숨김 처리를 끝내야 내용이 깜빡이지 않습니다.
  useLayoutEffect(() => {
    const reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion || typeof IntersectionObserver === "undefined") return;

    document.documentElement.classList.add("reveal-enabled");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add(SHOWN_CLASS);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );

    const scan = () => {
      document.querySelectorAll(SELECTORS).forEach((el) => {
        if (el.classList.contains(REVEAL_CLASS)) return;
        el.classList.add(REVEAL_CLASS);

        // 같은 줄에 나란히 놓인 카드들은 순서대로 조금씩 늦게 나타나게 합니다.
        const siblings = el.parentElement ? Array.from(el.parentElement.children) : [];
        const order = siblings.indexOf(el);
        if (order > 0) {
          el.style.transitionDelay = `${Math.min(order * 70, 280)}ms`;
        }
        // 한 프레임 뒤에 관찰을 시작해야, 이미 화면에 보이는 요소도
        // 숨김 → 표시 전환이 실제로 재생됩니다(첫 화면 문구 포함).
        window.requestAnimationFrame(() =>
          window.requestAnimationFrame(() => observer.observe(el))
        );
      });
    };

    // 빠르게 스크롤하면 관찰이 시작되기 전에 지나쳐 버리는 요소가 생깁니다.
    // 화면에 걸쳐 있거나 이미 지나간 요소는 바로 보이게 합니다.
    const sweep = () => {
      document
        .querySelectorAll(`.${REVEAL_CLASS}:not(.${SHOWN_CLASS})`)
        .forEach((el) => {
          if (el.getBoundingClientRect().top < window.innerHeight) {
            el.classList.add(SHOWN_CLASS);
            observer.unobserve(el);
          }
        });
    };

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        sweep();
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    scan();
    // 데이터를 나중에 불러오는 목록(뉴스 등)도 잡히도록 변화를 감시합니다.
    const mutation = new MutationObserver(() => window.requestAnimationFrame(scan));
    mutation.observe(document.body, { childList: true, subtree: true });

    // 목록이 늦게 그려지는 경우까지 대비해 잠시 동안 주기적으로 확인합니다.
    const sweepTimer = window.setInterval(sweep, 400);
    const stopSweep = window.setTimeout(() => window.clearInterval(sweepTimer), 6000);

    return () => {
      observer.disconnect();
      mutation.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      window.clearInterval(sweepTimer);
      window.clearTimeout(stopSweep);
    };
  }, [location.pathname]);

  return null;
}
